import { useMemo } from "react";

import { useFormatCurrency } from "@krea/shared/hooks/useFormatCurrency";
import type { Currency } from "@krea/shared/types/common";

import type { IFactoringBid } from "@/features/lender-responses/types/lender-response";
import { FormatNumber } from "@/utils";

import type { IBidDetailsResponse } from "../../BidDetails";
import { BidDetailsListItem, CheckedField, EmptyField } from "../../bidHelpers";

interface IFactoringBidProps {
  bid: IFactoringBid;
  currency: Currency;
}

export const FactoringBid = ({
  bid,
  currency,
}: IFactoringBidProps): IBidDetailsResponse => {
  const [formattedCurrencyValue] = useFormatCurrency({ currency });

  const amount = bid.amount;
  const comparableMonthlyInterest = bid.comparableMonthlyInterestRate;

  let paymentTermsDaysText = "";

  if (bid.paymentTermsDays) {
    paymentTermsDaysText = ` / ${bid.paymentTermsDays} days`;
  }

  const summary = useMemo(() => {
    return (
      <div className="flex w-full flex-col gap-2 xl:grid xl:grid-cols-4 xl:gap-4">
        <div className="flex flex-col gap-4">
          <BidDetailsListItem
            variant="summary"
            title="Amount"
            value={amount ? formattedCurrencyValue(amount) : <EmptyField />}
          />
        </div>
        <div className="flex flex-col gap-4">
          <BidDetailsListItem
            variant="summary"
            title="Credit fee"
            value={
              paymentTermsDaysText && bid.commissionPercent ? (
                `${FormatNumber(bid.commissionPercent)}%${paymentTermsDaysText}`
              ) : (
                <EmptyField />
              )
            }
          />
        </div>

        <div className="flex flex-col gap-4">
          <BidDetailsListItem
            variant="summary"
            title="With regress"
            value={bid.withRegress ? <CheckedField /> : <EmptyField />}
          />
        </div>

        <div className="flex flex-col gap-4">
          <BidDetailsListItem
            variant="summary"
            title="Effective monthly interest"
            value={
              comparableMonthlyInterest ? (
                `${FormatNumber(comparableMonthlyInterest)}%`
              ) : (
                <EmptyField />
              )
            }
          />
        </div>
      </div>
    );
  }, [
    amount,
    formattedCurrencyValue,
    paymentTermsDaysText,
    bid.commissionPercent,
    bid.withRegress,
    comparableMonthlyInterest,
  ]);

  const details = useMemo(() => {
    return (
      <>
        <div>
          {!!bid.purchaseLimit && (
            <BidDetailsListItem
              title="Purchase limit"
              value={formattedCurrencyValue(bid.purchaseLimit)}
            />
          )}

          {!!bid.purchasedAmountPercent && (
            <BidDetailsListItem
              title="Financing rate (in %)"
              value={`${FormatNumber(bid.purchasedAmountPercent)}%`}
            />
          )}

          {!!bid.regressNumDaysAfterDueDate && (
            <BidDetailsListItem
              title="Regress number of days"
              value={bid.regressNumDaysAfterDueDate}
            />
          )}
          {!!bid.averageMonthlyCost && (
            <BidDetailsListItem
              title="Monthly cost"
              value={formattedCurrencyValue(bid.averageMonthlyCost)}
            />
          )}
          {!!bid.monthlyFee && (
            <BidDetailsListItem
              title="Monthly fixed fee"
              value={formattedCurrencyValue(bid.monthlyFee)}
            />
          )}

          {!!bid.setupFee && (
            <BidDetailsListItem
              title="Setup fee"
              value={formattedCurrencyValue(bid.setupFee)}
            />
          )}

          {!!bid.limitFeePerYearPercent && (
            <BidDetailsListItem
              title="Limit fee (per year)"
              value={`${FormatNumber(bid.limitFeePerYearPercent)}%`}
            />
          )}

          {!!bid.handlingFeePerInvoice && (
            <BidDetailsListItem
              title="Handling Fee per invoice"
              value={formattedCurrencyValue(bid.handlingFeePerInvoice)}
            />
          )}
        </div>
        <div className="mt-4">
          All fees are included in total paid out amount
        </div>
      </>
    );
  }, [bid, formattedCurrencyValue]);

  return {
    summary,
    details,
  };
};
